import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ChevronDown, MapPin, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { BookingWidget } from "../components/BookingWidget";
import { Seo } from "../components/Seo";
import { useLang } from "../i18n";

const DEST_IDS = ["nice-airport", "cannes-croisette", "sophia", "grasse", "monaco", "antibes"];
const HERO_IMG = "https://images.pexels.com/photos/33353497/pexels-photo-33353497.jpeg?auto=compress&cs=tinysrgb&w=2000";

export default function Mougins() {
  const { t, ta, lang } = useLang();
  const [locations, setLocations] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [faqOpen, setFaqOpen] = useState(0);

  const whyItems = ta("mougins.why_items");
  const faqs = ta("mougins.faq_items");

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    DEST_IDS.forEach((id) => {
      api.post("/quote", { pickup_id: "mougins", dropoff_id: id })
        .then((r) => setQuotes((q) => ({ ...q, [id]: r.data })))
        .catch(() => {});
    });
  }, []);

  const loc = (id) => locations.find((l) => l.id === id);

  return (
    <div className="min-h-screen bg-sand" data-testid="mougins-page">
      <Seo
        title={t("mougins.meta_title")}
        description={t("mougins.meta_desc")}
        path="/chauffeur-prive-mougins"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "WebPage", name: t("mougins.meta_title"), description: t("mougins.meta_desc"), inLanguage: lang },
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}
      />
      <Navbar />

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden" data-testid="mougins-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt={t("mougins.h1")} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-sand/70 via-sand/85 to-sand" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-36 sm:px-6 lg:grid-cols-12 lg:items-center lg:px-8 lg:pb-28 lg:pt-44">
          <div className="lg:col-span-7">
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="overline-label mb-4">{t("mougins.overline")}</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              {t("mougins.h1")}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg">
              {t("mougins.subtitle")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-8 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-navy">
                <ShieldCheck size={15} className="text-azure" /> {t("mougins.badge1")}
              </span>
              <span className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-navy">
                <MapPin size={15} className="text-azure" /> {t("mougins.badge2")}
              </span>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
            className="lg:col-span-5">
            <BookingWidget defaultPickupText="Mougins" />
          </motion.div>
        </div>
      </section>

      {/* ---------------- DESTINATIONS ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="mougins-destinations">
        <Reveal>
          <p className="overline-label mb-4">{t("mougins.dest_overline")}</p>
          <h2 className="max-w-2xl font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            {t("mougins.dest_title")}
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DEST_IDS.map((id, i) => (
            <Reveal key={id} delay={i * 0.06} className="h-full">
              <Link
                to={`/reservation?from=mougins&to=${id}`}
                data-testid={`mougins-dest-${id}`}
                className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-azure-soft text-azure">
                    <MapPin size={19} />
                  </span>
                  <p className="font-serif text-lg font-semibold leading-snug text-navy">{loc(id)?.label || "…"}</p>
                </div>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-navy/55">{loc(id)?.sublabel || ""}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-sm font-bold text-navy" data-testid={`mougins-price-${id}`}>
                    {quotes[id] ? t("mougins.from_price", { price: quotes[id].berline.price_eur }) : "…"}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm font-bold text-azure">
                    {t("mougins.book_route")}
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- POURQUOI ---------------- */}
      <section className="bg-white" data-testid="mougins-why">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <Reveal>
            <p className="overline-label mb-4">{t("mougins.why_overline")}</p>
            <h2 className="max-w-2xl font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              {t("mougins.why_title")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {whyItems.map((w, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex h-full gap-4 rounded-3xl border border-slate-100 bg-sand p-7">
                  <CheckCircle2 size={22} className="mt-1 shrink-0 text-azure" />
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-navy">{w.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-navy/60">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="mx-auto max-w-3xl px-4 py-24 sm:px-6" data-testid="mougins-faq">
        <Reveal>
          <p className="overline-label mb-4">{t("mougins.faq_overline")}</p>
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl">
            {t("mougins.faq_title")}
          </h2>
        </Reveal>
        <div className="mt-10 space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white" data-testid={`mougins-faq-${i}`}>
              <button
                onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                data-testid={`mougins-faq-toggle-${i}`}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <span className="text-sm font-semibold text-navy sm:text-base">{f.q}</span>
                <ChevronDown size={18} className={`shrink-0 text-azure transition-transform duration-300 ${faqOpen === i ? "rotate-180" : ""}`} />
              </button>
              {faqOpen === i && (
                <p className="px-6 pb-5 text-sm leading-relaxed text-navy/65" data-testid={`mougins-faq-answer-${i}`}>{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-navy p-10 text-center sm:p-14" data-testid="mougins-cta">
          <h2 className="font-serif text-2xl font-semibold text-white sm:text-3xl">{t("cta.title")}</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-white/60">{t("cta.text")}</p>
          <Link
            to="/reservation?from=mougins&to=nice-airport"
            data-testid="mougins-cta-book"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-azure px-9 py-4 text-sm font-bold text-white transition-colors hover:bg-azure-hover"
          >
            {t("cta.book")} <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
